import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faThumbTack, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { usePinnedResearchFacts } from "@/pages/Private/redux/deepResearch/usePinnedResearchFacts";

interface PinnedFactsRowProps {
	/** The lead the sequence is written for — pins are collected per prospect. */
	prospectId: number;
	disabled?: boolean;
}

/**
 * The research facts the user pinned for this lead ("Mention in sequence" in the
 * Deep Research panel), shown below the "Tell AI what to write" input.
 *
 * The facts are already handed to the copywriter server-side; this row exists so
 * the user can see *that* before generating, and drop one they changed their mind
 * about. Renders nothing when nothing is pinned, so the panel stays unchanged for
 * everyone who never pins.
 */
export const PinnedFactsRow: FunctionComponent<PinnedFactsRowProps> = ({
	prospectId,
	disabled,
}) => {
	const { t } = useTranslation();
	const { pinnedFacts, isPinning, unpinFact } = usePinnedResearchFacts(prospectId);

	if (pinnedFacts.length === 0) {
		return null;
	}

	return (
		<div
			className="flex flex-col gap-1.5 rounded-lg border border-border-light bg-brand-white px-3 py-2"
			data-testid="pinned-facts-row"
		>
			<span className="flex items-center gap-2 text-xs font-semibold text-text">
				<Icon className="shrink-0 text-brand-light" icon={faThumbTack} />
				{t("messages.generateBar.pinnedFacts.label", { count: pinnedFacts.length })}
			</span>

			<div className="flex flex-wrap gap-1.5">
				{pinnedFacts.map((fact) => (
					<span
						key={fact.id}
						className="flex max-w-full items-center gap-1.5 rounded-md border border-border-light bg-white px-2 py-1 text-[11px]"
						data-testid={`pinned-fact-chip-${fact.id}`}
					>
						<span className="min-w-0 truncate text-text-meta" title={fact.factText}>
							{fact.factLabel || fact.factText}
						</span>
						<button
							aria-label={t("messages.generateBar.pinnedFacts.remove") || undefined}
							className="shrink-0 cursor-pointer text-text-meta hover:text-status-error disabled:cursor-not-allowed disabled:opacity-50"
							disabled={disabled || isPinning}
							title={t("messages.generateBar.pinnedFacts.remove") || undefined}
							type="button"
							onClick={() => void unpinFact(fact.id)}
						>
							<Icon icon={faXmark} />
						</button>
					</span>
				))}
			</div>
		</div>
	);
};
